import { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { createList, updateList } from '../../utils/listsApi';
import { Button } from '../ui';

const MAX_NAME = 30;
const MAX_DESC = 150;

export default function CreateListModal({
  onClose,
  onCreated,
  onUpdated,
  editMode = false,
  listId = null,
  initialName = '',
  initialDescription = '',
  initialPrivate = true
}) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [isPrivate, setIsPrivate] = useState(initialPrivate);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setName(initialName);
    setDescription(initialDescription);
    setIsPrivate(initialPrivate);
  }, [initialName, initialDescription, initialPrivate]);

  const nameLen = name.length;
  const descLen = description.length;
  const valid = name.trim().length >= 1 && name.trim().length <= MAX_NAME && descLen <= MAX_DESC;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setError('');
    setSubmitting(true);
    try {
      if (editMode && listId) {
        const list = await updateList(listId, {
          name: name.trim(),
          description: description.trim(),
          isPrivate: isPrivate
        });
        onUpdated?.(list);
      } else {
        const list = await createList({
          name: name.trim(),
          description: description.trim().slice(0, MAX_DESC),
          isPrivate: isPrivate
        });
        onCreated?.(list);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-background-card dark:bg-background-primary border border-border-primary rounded-xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <h2 className="text-lg font-bold text-text-primary">
            {editMode ? 'Edit List' : 'Create New List'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-background-secondary text-text-secondary">
            <FaTimes />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <p className="text-sm text-red-500 bg-red-500/10 px-3 py-2 rounded-lg">{error}</p>
          )}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Title</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, MAX_NAME))}
              placeholder="Enter a list name"
              className="w-full px-3 py-2 rounded-lg bg-background-secondary border border-border-primary text-text-primary placeholder-text-tertiary focus:ring-2 focus:ring-button-primary-bg focus:outline-none"
              maxLength={MAX_NAME}
              autoFocus
            />
            <p className="text-right text-xs text-text-tertiary mt-1">{nameLen}/{MAX_NAME}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESC))}
              placeholder="Describe your list"
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-background-secondary border border-border-primary text-text-primary placeholder-text-tertiary focus:ring-2 focus:ring-button-primary-bg focus:outline-none resize-none"
              maxLength={MAX_DESC}
            />
            <p className="text-right text-xs text-text-tertiary mt-1">{descLen}/{MAX_DESC}</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              className="rounded border-border-primary text-green-500 focus:ring-button-primary-bg"
            />
            <span className="text-sm text-text-primary">Private</span>
          </label>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!valid || submitting}
              className="flex-1"
            >
              {submitting ? '...' : editMode ? 'Save' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
